import { motion } from "framer-motion";
import { Bot, ScanSearch, Recycle, Handshake, Landmark, TrendingUp, BarChart3 } from "lucide-react";
import ChapterMark from "@/components/ChapterMark";
import WhaleShark from "@/components/WhaleShark";

const COMPETITORS = [
    {
        name: "Seabin",
        approach: "Fixed collection point",
        difference: "A mobile fleet that patrols different areas",
    },
    {
        name: "RanMarine WasteShark",
        approach: "Aquatic waste collection",
        difference: "An autonomous fleet delivered as a council service",
    },
    {
        name: "Clearbot",
        approach: "Autonomous waste collection and data",
        difference: "Sydney-focused, with a circular economy and Indigenous partnership built in",
    },
    {
        name: "Traditional cleanup",
        approach: "Periodic, manual removal",
        difference: "Continuous autonomous collection and monitoring",
    },
];

const EDGES = [
    {
        icon: Bot,
        title: "Autonomous fleet",
        detail: "Multiple Guardians working across a waterway, coordinated as one service.",
    },
    {
        icon: ScanSearch,
        title: "Microplastic focus",
        detail: "Designed to target both visible litter and smaller plastic pollution.",
    },
    {
        icon: Recycle,
        title: "Circular economy",
        detail: "Recovered materials: 75% sold to Australian recyclers, 25% allocated to Indigenous artists.",
    },
    {
        icon: Handshake,
        title: "Indigenous partnership",
        detail: "Indigenous design, consultation and material allocation built into the business model.",
    },
    {
        icon: Landmark,
        title: "Council-as-a-service",
        detail: "Councils pay for an ongoing cleaning and monitoring service, rather than simply buying a robot.",
    },
];

const INVESTOR_CARDS = [
    {
        icon: TrendingUp,
        title: "Recurring revenue",
        stat: "$8m a year by Year 6",
        detail: "Target council contract of ~$150,000 per council, per year, plus approximately $1.23m of recycling revenue over ten years.",
    },
    {
        icon: Bot,
        title: "Scalable fleet",
        stat: "9 to 120 Guardians",
        detail: "Thirteen full nine-Guardian fleets plus three additional units by Year 6. Sydney remains the target market for the first ten years.",
    },
    {
        icon: BarChart3,
        title: "Measurable impact",
        stat: "7,375 tonnes removed",
        detail: "Over ten years: 4,425 t recyclable, 1,106 t allocated to Indigenous artists, 3,319 t sold and recovered.",
    },
];

export default function Difference() {
    return (
        <section id="difference" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32" data-testid="difference-section">
            <WhaleShark
                className="right-[-160px] top-16 w-[460px] rotate-6"
                opacity={0.08}
                testid="difference-whale"
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="04" label="The Difference" testid="difference-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="difference-heading">
                        Not just another water-cleaning robot
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="difference-intro">
                        RiverGuardian is not hardware for sale. It is a service model: an autonomous fleet,
                        a circular economy and a cultural partnership, contracted by councils year after year.
                    </p>
                </div>

                <div className="mt-12 overflow-x-auto border border-[#12283C] rounded-xl" data-testid="difference-table-wrapper">
                    <table className="w-full text-base" data-testid="difference-table">
                        <thead>
                            <tr className="bg-[#06182A] text-left">
                                <th className="p-4 font-semibold text-slate-400 w-1/5">Competitor</th>
                                <th className="p-4 font-semibold text-slate-400 w-2/5">Their approach</th>
                                <th className="p-4 font-semibold text-sky-200 w-2/5">The RiverGuardian difference</th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPETITORS.map((row, i) => (
                                <tr
                                    key={row.name}
                                    className={`${i % 2 === 0 ? "bg-[#020D18]" : "bg-[#041222]"} transition-transform duration-300 hover:translate-x-1`}
                                    data-testid={`difference-row-${i}`}
                                >
                                    <td className="p-4 font-semibold text-slate-200 align-top">{row.name}</td>
                                    <td className="p-4 text-slate-400 align-top">{row.approach}</td>
                                    <td className="p-4 text-sky-200 font-medium align-top border-l-2 border-sky-400/40">{row.difference}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-14" data-testid="difference-edges">
                    <p className="text-sm font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5">
                        Our competitive edge
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {EDGES.map((edge, i) => {
                            const Icon = edge.icon;
                            return (
                                <motion.div
                                    key={edge.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.45, delay: i * 0.07 }}
                                    className="bg-[#06182A] border border-[#12283C] rounded-xl p-6 hover:-translate-y-1 hover:border-sky-400/50 transition-[transform,border-color] duration-300"
                                    data-testid={`difference-edge-${i}`}
                                >
                                    <span className="w-11 h-11 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-sky-200" />
                                    </span>
                                    <p className="mt-4 text-lg font-semibold text-slate-100">{edge.title}</p>
                                    <p className="mt-2 text-base text-slate-400 leading-relaxed">{edge.detail}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6 }}
                    className="mt-16 border-y border-[#12283C] py-12 text-center"
                    data-testid="difference-key-message"
                >
                    <p className="font-script text-3xl text-emerald-300/90">the key message</p>
                    <p className="mt-4 font-display italic font-semibold text-3xl md:text-5xl leading-tight max-w-4xl mx-auto">
                        We don't just build robots, <span className="text-sky-200">we provide an ongoing waterway-cleaning service.</span>
                    </p>
                </motion.div>

                <div className="mt-16" data-testid="investor-block">
                    <ChapterMark index="" label="Why an investor cares" testid="investor-eyebrow" />
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {INVESTOR_CARDS.map((card, i) => {
                            const Icon = card.icon;
                            return (
                                <motion.div
                                    key={card.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.45, delay: i * 0.08 }}
                                    className="bg-[#05251C] border border-emerald-900/60 rounded-xl p-6 hover:border-emerald-400/50 transition-colors"
                                    data-testid={`investor-card-${i}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon className="w-5 h-5 text-emerald-300" />
                                        <p className="text-sm font-semibold tracking-[0.2em] uppercase text-emerald-300">{card.title}</p>
                                    </div>
                                    <p className="mt-4 font-display font-semibold text-3xl text-slate-50">{card.stat}</p>
                                    <p className="mt-3 text-sm text-slate-300 leading-relaxed">{card.detail}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                    <p className="mt-4 text-sm text-slate-500" data-testid="investor-note">
                        Figures from the RiverGuardian expected-case ten-year model. See the Financials chapter for scenarios and assumptions.
                    </p>
                </div>
            </div>
        </section>
    );
}
